import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Empty, Alert, Table, Tooltip } from 'antd';
import { ArrowLeftOutlined, WalletOutlined, DollarOutlined, CopyOutlined, ReloadOutlined, WarningOutlined, InfoCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  error: '#EF4444',
  warning: '#F59E0B',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};

  /* Fix Alert text colors */
  .ant-alert {
    color: ${COLORS.textPrimary} !important;
    
    .ant-alert-message {
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-alert-description {
      color: ${COLORS.textSecondary} !important;
      
      p, ul, li {
        color: ${COLORS.textSecondary} !important;
      }
    }
  }

  /* Fix Empty component text */
  .ant-empty {
    .ant-empty-description {
      color: ${COLORS.textSecondary} !important;
    }
  }

  /* Fix pagination text */
  .ant-pagination {
    color: ${COLORS.textPrimary} !important;
    
    .ant-pagination-item {
      a {
        color: ${COLORS.textPrimary} !important;
      }
      
      &.ant-pagination-item-active {
        a {
          color: ${COLORS.accent} !important;
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      .ant-pagination-item-link {
        color: ${COLORS.textPrimary} !important;
      }
    }
    
    .ant-pagination-options {
      color: ${COLORS.textPrimary} !important;
      
      .ant-select-selector {
        color: ${COLORS.textPrimary} !important;
      }
    }
    
    .ant-pagination-total-text {
      color: ${COLORS.textSecondary} !important;
    }
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
`;

const CurrencyIcon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const ExplorerIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 6px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const ExplorerLink = styled.a`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  color: ${COLORS.accent};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const GasBalanceTable = styled(Table)`
  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
  }

  .ant-table-tbody > tr > td {
    background: transparent !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary};
  }

  .ant-table-tbody > tr:hover > td {
    background: rgba(255, 255, 255, 0.02) !important;
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none !important;
  }
`;

const GasStation: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState<any>(null);
  const [gasWallet, setGasWallet] = useState<any>(null);
  const [gasBalances, setGasBalances] = useState<any>(null);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [loadingGasWallet, setLoadingGasWallet] = useState(false);
  const [hasGasWalletId, setHasGasWalletId] = useState<boolean | null>(null);
  const [blockchainNetworks, setBlockchainNetworks] = useState<any[]>([]);

  const fetchCompanyData = async () => {
    if (!companyId) return;

    try {
      const companyResponse = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_COMPANY_DETAILS.replace('{companyId}', companyId)
      );

      if (companyResponse.data?.status === 'success' && companyResponse.data?.data) {
        const data = companyResponse.data.data;
        setCompanyData(data);
        const hasGasWallet = !!data.gasWalletId;
        setHasGasWalletId(hasGasWallet);
        return hasGasWallet;
      }
    } catch (companyError: any) {
      console.error('Error fetching company details:', companyError);
      message.error('Failed to load company information');
    }
    return false;
  };

  const fetchGasWallet = async () => {
    if (!companyId) return;

    try {
      setLoadingGasWallet(true);
      // Fetch gas wallet details
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_GAS_WALLET.replace('{companyId}', companyId)
      );

      if (response.data?.status === 'success' && response.data?.data) {
        setGasWallet(response.data.data.wallet);
        setGasBalances(response.data.data.gasBalances);
      }
    } catch (error: any) {
      // Only show error if we know a gas wallet should exist
      if (hasGasWalletId === true) {
        message.error(error.response?.data?.message || 'Failed to fetch gas wallet');
      }
      console.error('Error fetching gas wallet:', error);
    } finally {
      setLoadingGasWallet(false);
    }
  };

  const initializeData = async () => {
    setLoading(true);

    // First load company data (synchronous)
    const hasGasWallet = await fetchCompanyData();

    // If company has gas wallet, load gas wallet data asynchronously
    if (hasGasWallet) {
      fetchGasWallet();
    }

    setLoading(false);
  };

  const refreshBalances = async () => {
    if (!companyId) return;

    try {
      setLoadingBalances(true);
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_GAS_WALLET.replace('{companyId}', companyId)
      );

      if (response.data?.status === 'success' && response.data?.data) {
        setGasBalances(response.data.data.gasBalances);
        message.success('Gas balances refreshed');
      }
    } catch (error: any) {
      message.error('Failed to refresh gas balances');
      console.error('Error refreshing gas balances:', error);
    } finally {
      setLoadingBalances(false);
    }
  };

  const fetchBlockchainNetworks = async () => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS}?status=ACTIVE&limit=100`);
      if (response.data?.status === 'success' && response.data?.data?.itemsList) {
        setBlockchainNetworks(response.data.data.itemsList);
      }
    } catch (error: any) {
      console.error('Error fetching blockchain networks:', error);
    }
  };

  useEffect(() => {
    initializeData();
    fetchBlockchainNetworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  // Same explorer-link pattern already used on the Wallet Details page, for consistency.
  const getEVMExplorerUrl = (network: any, address: string) => {
    if (!network?.explorerUrl || !address) return null;
    const baseUrl = network.explorerUrl.startsWith('http')
      ? network.explorerUrl
      : `https://${network.explorerUrl}`;
    return `${baseUrl}/address/${address}`;
  };

  const getBitcoinExplorerUrl = (address: string) => (address ? `https://blockstream.info/address/${address}` : null);
  const getSolanaExplorerUrl = (address: string) => (address ? `https://solscan.io/account/${address}` : null);
  const getTronExplorerUrl = (address: string) => (address ? `https://tronscan.org/#/address/${address}` : null);

  const evmNetworksWithExplorers = blockchainNetworks.filter(
    (network) => network.blockchainType === 'EVM' && network.explorerUrl && network.status === 'ACTIVE'
  );

  const renderEVMExplorerIcons = (address: string) => {
    if (!address || evmNetworksWithExplorers.length === 0) return null;
    return (
      <Space size={4}>
        {evmNetworksWithExplorers.map((network) => {
          const explorerUrl = getEVMExplorerUrl(network, address);
          if (!explorerUrl) return null;
          return (
            <Tooltip key={network.id} title={`View on ${network.name}`}>
              <ExplorerLink href={explorerUrl} target="_blank" rel="noopener noreferrer">
                {network.iconUrl ? (
                  <ExplorerIcon src={network.iconUrl} alt={network.name} />
                ) : (
                  <LinkOutlined style={{ fontSize: 14 }} />
                )}
              </ExplorerLink>
            </Tooltip>
          );
        })}
      </Space>
    );
  };

  const renderSingleExplorerLink = (url: string | null, label: string) => {
    if (!url) return null;
    return (
      <Tooltip title={`View on ${label}`}>
        <ExplorerLink href={url} target="_blank" rel="noopener noreferrer">
          <LinkOutlined style={{ fontSize: 14 }} />
        </ExplorerLink>
      </Tooltip>
    );
  };

  // Per-row explorer link for the Gas Balances table, so each network's row links to its own
  // explorer even though EVM rows share the same underlying address across networks.
  const getRowExplorerUrl = (row: { key: string; chainName: string }, address: {
    evm?: string; solana?: string; tron?: string; bitcoin?: string;
  }): string | null => {
    if (row.key === 'solana') return address.solana ? getSolanaExplorerUrl(address.solana) : null;
    if (row.key === 'tron') return address.tron ? getTronExplorerUrl(address.tron) : null;
    if (row.key.startsWith('evm-') && address.evm) {
      const network = blockchainNetworks.find((n) => n.id === row.key.replace('evm-', ''));
      return network ? getEVMExplorerUrl(network, address.evm) : null;
    }
    return null;
  };

  // Show loading state while fetching
  if (loading || hasGasWalletId === null) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  // Show "Pending Gas Wallet" only if we've confirmed there's no gasWalletId
  if (!gasWallet && hasGasWalletId === false) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/kivu/companies/${companyId}`)}>
            Back to Company
          </Button>
          <Typography.Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Gas Station
          </Typography.Title>
        </HeaderContainer>
        <Alert
          message="Pending Gas Wallet"
          description="This merchant company does not have a gas wallet assigned yet. Gas wallets are assigned when a merchant is activated by an administrator."
          type="warning"
          showIcon
          style={{ background: COLORS.bgSecondary, borderColor: COLORS.border }}
        />
      </PageContainer>
    );
  }

  // If gasWalletId exists but we couldn't fetch details, show error
  // Show loading state while fetching initial company data
  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  // Show company header even if gas wallet fails to load
  const renderCompanyHeader = (showRefreshButton = false) => (
    <HeaderContainer>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/kivu/companies/${companyId}`)}>
        Back to Company
      </Button>
      <Space>
        {companyData?.logoUrl && (
          <img
            src={companyData.logoUrl}
            alt="Company Logo"
            style={{ width: 40, height: 40, borderRadius: '8px', objectFit: 'cover' }}
          />
        )}
        <div>
          <Typography.Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Gas Station
          </Typography.Title>
          {companyData?.name && (
            <Typography.Text style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
              {companyData.name}
            </Typography.Text>
          )}
        </div>
        {showRefreshButton && (
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshBalances}
            loading={loadingBalances}
          >
            Refresh Balances
          </Button>
        )}
      </Space>
    </HeaderContainer>
  );

  // Show error state when gas wallet failed to load (not loading anymore) but company data is available
  if (!gasWallet && !loadingGasWallet && hasGasWalletId === true) {
    return (
      <PageContainer>
        {renderCompanyHeader()}
        <Alert
          message="Error Loading Gas Wallet"
          description="A gas wallet is assigned to this merchant, but we couldn't load its details. Please try refreshing the page or contact support if the issue persists."
          type="error"
          showIcon
          style={{ background: COLORS.bgSecondary, borderColor: COLORS.border }}
        />
      </PageContainer>
    );
  }

  // Show loading state when gas wallet data is being fetched
  if (loadingGasWallet && hasGasWalletId === true) {
    return (
      <PageContainer>
        {renderCompanyHeader()}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin size="large" tip="Loading gas wallet details..." />
        </div>
      </PageContainer>
    );
  }

  // Show empty state when no gas wallet is assigned
  if (hasGasWalletId === false) {
    return (
      <PageContainer>
        {renderCompanyHeader()}
        <Empty
          description="No gas wallet assigned to this company"
          style={{ color: COLORS.textSecondary }}
        />
      </PageContainer>
    );
  }

  const lowGasBalances = [
    ...(gasBalances?.evmGasBalances?.filter((balance: any) => balance.isLowGas) || []),
    ...(gasBalances?.solanaGasBalance && gasBalances.solanaGasBalance.isLowGas ? [gasBalances.solanaGasBalance] : []),
    ...(gasBalances?.tronGasBalance && gasBalances.tronGasBalance.isLowGas ? [{ ...gasBalances.tronGasBalance, currencySymbol: 'TRX', blockchainNetworkName: 'TRON' }] : []),
  ];

  // Every chain only ever contributes one gas-token row (the native token), so grouping them
  // into a separate bordered card per chain was pure visual overhead. One flat, compact table
  // reads far more clearly than a stack of near-identical single-row cards.
  const gasBalanceRows = [
    ...(gasBalances?.evmGasBalances || []).map((b: any) => ({
      key: `evm-${b.blockchainNetworkId}`,
      chainName: b.blockchainNetworkName,
      chainIconUrl: b.blockchainNetworkIconUrl,
      currencySymbol: b.currencySymbol,
      currencyIconUrl: b.currencyIconUrl,
      balance: b.balance,
      balanceUsd: b.balanceUsd,
      isLowGas: b.isLowGas,
      error: b.error,
    })),
    ...(gasBalances?.solanaGasBalance
      ? [{
          key: 'solana',
          chainName: 'Solana',
          chainIconUrl: null,
          currencySymbol: 'SOL',
          currencyIconUrl: null,
          balance: gasBalances.solanaGasBalance.balance,
          balanceUsd: gasBalances.solanaGasBalance.balanceUsd,
          isLowGas: gasBalances.solanaGasBalance.isLowGas,
          error: gasBalances.solanaGasBalance.error,
        }]
      : []),
    ...(gasBalances?.tronGasBalance
      ? [{
          key: 'tron',
          chainName: 'TRON',
          chainIconUrl: null,
          currencySymbol: 'TRX',
          currencyIconUrl: null,
          balance: gasBalances.tronGasBalance.balance,
          balanceUsd: gasBalances.tronGasBalance.balanceUsd,
          isLowGas: gasBalances.tronGasBalance.isLowGas,
          error: gasBalances.tronGasBalance.error,
        }]
      : []),
  ];

  const gasBalanceColumns = [
    {
      title: 'Chain / Currency',
      key: 'chain',
      render: (_: any, row: (typeof gasBalanceRows)[number]) => (
        <Space>
          {(row.currencyIconUrl || row.chainIconUrl) && (
            <CurrencyIcon src={row.currencyIconUrl || row.chainIconUrl || ''} alt={row.currencySymbol} />
          )}
          <div>
            <div style={{ color: COLORS.textPrimary, fontWeight: 500 }}>{row.currencySymbol}</div>
            <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>{row.chainName}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Balance',
      key: 'balance',
      align: 'right' as const,
      render: (_: any, row: (typeof gasBalanceRows)[number]) => (
        <span style={{ fontFamily: 'monospace' }}>
          {Number(row.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}{' '}
          {row.currencySymbol}
        </span>
      ),
    },
    {
      title: 'USD Value',
      key: 'balanceUsd',
      align: 'right' as const,
      render: (_: any, row: (typeof gasBalanceRows)[number]) => (
        <span>
          ${Number(row.balanceUsd || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, row: (typeof gasBalanceRows)[number]) =>
        row.error ? (
          <Tooltip title={row.error}>
            <Tag color="red" icon={<WarningOutlined />}>Error</Tag>
          </Tooltip>
        ) : row.isLowGas ? (
          <Tag color="orange" icon={<WarningOutlined />}>Low Gas</Tag>
        ) : (
          <Tag color="green">OK</Tag>
        ),
    },
    {
      title: 'Explorer',
      key: 'explorer',
      render: (_: any, row: (typeof gasBalanceRows)[number]) => {
        const url = getRowExplorerUrl(row, {
          evm: gasWallet?.evmAddress,
          solana: gasWallet?.solanaAddress,
          tron: gasWallet?.tronAddress,
        });
        if (!url) return '-';
        return (
          <ExplorerLink href={url} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> View
          </ExplorerLink>
        );
      },
    },
  ];

  return (
    <PageContainer>
      {renderCompanyHeader(true)}

      {lowGasBalances.length > 0 && (
        <Alert
          message="Low Gas Warning"
          description={
            <div style={{ color: COLORS.textSecondary }}>
              <p style={{ color: COLORS.textSecondary, marginBottom: 8 }}>The following gas balances are below $1 USD and may need to be topped up:</p>
              <ul style={{ marginTop: 8, marginBottom: 0, color: COLORS.textSecondary }}>
                {lowGasBalances.map((balance: any, index: number) => (
                  <li key={index} style={{ color: COLORS.textSecondary }}>
                    {balance.currencySymbol} on {balance.blockchainNetworkName}: ${Number(balance.balanceUsd || 0).toFixed(2)}
                  </li>
                ))}
              </ul>
            </div>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ 
            marginBottom: 24,
            background: `${COLORS.warning}10`,
            borderColor: COLORS.warning,
          }}
        />
      )}

      <StyledCard
        title={
          <Space>
            <WalletOutlined />
            <span>Gas Wallet Details</span>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="Wallet ID">
            <Space>
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {gasWallet.walletId || gasWallet.id}
              </span>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopy(gasWallet.walletId || gasWallet.id, 'Wallet ID')}
                style={{ color: COLORS.accent }}
              />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Member ID">{gasWallet.externalMemberId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Member Name">{gasWallet.externalMemberName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Member Email">{gasWallet.externalMemberEmail || '-'}</Descriptions.Item>
          {gasWallet.evmAddress && (
            <Descriptions.Item label="EVM Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {gasWallet.evmAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(gasWallet.evmAddress, 'EVM Address')}
                  style={{ color: COLORS.accent }}
                />
                {renderEVMExplorerIcons(gasWallet.evmAddress)}
              </Space>
            </Descriptions.Item>
          )}
          {gasWallet.solanaAddress && (
            <Descriptions.Item label="Solana Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {gasWallet.solanaAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(gasWallet.solanaAddress, 'Solana Address')}
                  style={{ color: COLORS.accent }}
                />
                {renderSingleExplorerLink(getSolanaExplorerUrl(gasWallet.solanaAddress), 'Solscan')}
              </Space>
            </Descriptions.Item>
          )}
          {gasWallet.tronAddress && (
            <Descriptions.Item label="TRON Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {gasWallet.tronAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(gasWallet.tronAddress, 'TRON Address')}
                  style={{ color: COLORS.accent }}
                />
                {renderSingleExplorerLink(getTronExplorerUrl(gasWallet.tronAddress), 'Tronscan')}
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </StyledCard>

      <StyledCard
        title={
          <Space>
            <DollarOutlined />
            <span>Gas Balances</span>
            {gasBalances?.totalUsd !== undefined && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                Total: ${Number(gasBalances.totalUsd).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Tag>
            )}
          </Space>
        }
      >
        {gasBalanceRows.length > 0 ? (
          <GasBalanceTable
            columns={gasBalanceColumns as any}
            dataSource={gasBalanceRows}
            rowKey="key"
            pagination={false}
            size="small"
          />
        ) : (
          <Empty
            description="No gas balances available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ color: COLORS.textSecondary }}
          />
        )}
      </StyledCard>

      <Alert
        message="About Gas Wallets"
        description={
          <div style={{ color: COLORS.textSecondary }}>
            <p style={{ color: COLORS.textSecondary, marginBottom: 8 }}>The gas wallet is used to fund child wallets with gas (native tokens) for EVM and Solana sweeps.</p>
            <p style={{ marginTop: 8, marginBottom: 0, color: COLORS.textSecondary }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              Low gas balances (less than $1 USD) are flagged with a warning. Ensure sufficient gas is available for sweep operations.
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ background: COLORS.bgSecondary, borderColor: COLORS.border }}
      />
    </PageContainer>
  );
};

export default GasStation;

